import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { convertFromUSD, formatMoney, getFxRates, subscribeFxRates } from "@/lib/currency";

/**
 * Studio Checkout — Monthly (₹999) or Annual (₹11,389 — 5% off).
 * All prices anchored to INR; other currencies are shown live-converted.
 *
 * Flow:
 *   1. Pick a billing cycle
 *   2. Enter email + name (needed for Razorpay prefill + receipt)
 *   3. Click Pay → backend creates Razorpay order → open Checkout.js
 *   4. Success → POST /payments/verify → receive `checkout_token`
 *   5. Redirect to /register with plan/billing/token in the URL
 */
export default function Checkout() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const plan = sp.get("plan") || "studio";

  const [cycle, setCycle] = useState("monthly"); // "monthly" | "annual"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [fx, setFx] = useState(getFxRates());

  useEffect(() => subscribeFxRates(setFx), []);

  const publicCurrency = (typeof window !== "undefined" && localStorage.getItem("kairosa_landing_currency")) || "INR";

  // INR anchors — mirror backend /payments/config
  const INR_MONTHLY = 999;
  const INR_ANNUAL = 11389; // 999 × 12 × 0.95 rounded

  const amountINR = cycle === "annual" ? INR_ANNUAL : INR_MONTHLY;

  const displayPrice = useMemo(() => {
    if (publicCurrency === "INR") return formatMoney(amountINR, "INR", { maximumFractionDigits: 0 });
    const inrRate = fx?.rates?.INR;
    if (!inrRate) return formatMoney(amountINR, "INR", { maximumFractionDigits: 0 });
    const usdEquiv = amountINR / inrRate;
    return formatMoney(convertFromUSD(usdEquiv, publicCurrency), publicCurrency, { maximumFractionDigits: 0 });
  }, [amountINR, publicCurrency, fx]);

  const savings = useMemo(() => {
    const yearlyIfMonthly = INR_MONTHLY * 12;
    const diff = yearlyIfMonthly - INR_ANNUAL;
    return diff; // ₹ saved
  }, []);

  const openRazorpay = async () => {
    if (!email || !name) {
      toast.error("Please enter your name and email first.");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/create-order", {
        plan,
        billing_cycle: cycle,
        customer_email: email,
        customer_name: name,
      });

      if (!window.Razorpay) {
        toast.error("Razorpay could not load. Please refresh and try again.");
        setBusy(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Kairosa",
        description: `Studio · ${cycle === "annual" ? "Annual (5% off)" : "Monthly"}`,
        image: "/favicon.ico",
        prefill: { email, name },
        theme: { color: "#e97035" },
        handler: async (resp) => {
          try {
            const { data: verified } = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan,
              billing_cycle: cycle,
            });
            toast.success("Payment successful. Let's set up your workspace.");
            const q = new URLSearchParams({
              plan,
              billing_cycle: verified.billing_cycle,
              token: verified.checkout_token,
              email,
              name,
            }).toString();
            nav(`/register?${q}`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed.");
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-6 py-14">
        <Link
          to="/"
          data-testid="checkout-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>

        <div className="mt-8 grid lg:grid-cols-5 gap-10">
          {/* Left — plan summary */}
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">Checkout · Studio</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">Pick your billing cycle.</h1>
            <p className="mt-3 text-muted-foreground max-w-lg">
              You're subscribing to the Studio plan — Core Platform, 3 modules, 3 team seats and the AI Assistant, all included. Cancel anytime.
            </p>

            <div className="mt-8 grid gap-3">
              <button
                type="button"
                data-testid="checkout-cycle-monthly"
                onClick={() => setCycle("monthly")}
                className={
                  "text-left rounded-xl border p-5 transition " +
                  (cycle === "monthly" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40")
                }
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl">Monthly</div>
                    <div className="text-xs text-muted-foreground mt-1">Billed every 30 days · reminder 10 days before due</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">
                      {publicCurrency === "INR"
                        ? formatMoney(INR_MONTHLY, "INR", { maximumFractionDigits: 0 })
                        : (fx?.rates?.INR
                            ? formatMoney(convertFromUSD(INR_MONTHLY / fx.rates.INR, publicCurrency), publicCurrency, { maximumFractionDigits: 0 })
                            : `₹${INR_MONTHLY}`)}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">per month</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                data-testid="checkout-cycle-annual"
                onClick={() => setCycle("annual")}
                className={
                  "text-left rounded-xl border p-5 transition relative " +
                  (cycle === "annual" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40")
                }
              >
                <span className="absolute -top-2 left-4 text-[9px] uppercase tracking-[0.22em] bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                  Save 5% · ₹{savings.toLocaleString("en-IN")}/yr
                </span>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-xl">Annual</div>
                    <div className="text-xs text-muted-foreground mt-1">Billed once a year · 5% discount applied</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">
                      {publicCurrency === "INR"
                        ? formatMoney(INR_ANNUAL, "INR", { maximumFractionDigits: 0 })
                        : (fx?.rates?.INR
                            ? formatMoney(convertFromUSD(INR_ANNUAL / fx.rates.INR, publicCurrency), publicCurrency, { maximumFractionDigits: 0 })
                            : `₹${INR_ANNUAL}`)}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">per year</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-8 space-y-2 text-sm text-muted-foreground">
              {["Core Platform + 3 modules", "3 team seats included", "AI Assistant (Claude Sonnet 4.5)", "Cancel anytime"].map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {f}
                </div>
              ))}
            </div>
          </div>

          {/* Right — payment */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-border bg-card p-6 sticky top-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-3">Order summary</div>
              <div className="flex items-baseline justify-between">
                <div className="font-display text-lg">Studio · {cycle === "annual" ? "Annual" : "Monthly"}</div>
                <div className="font-display text-3xl">{displayPrice}</div>
              </div>
              {publicCurrency !== "INR" && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Charged as ₹{amountINR.toLocaleString("en-IN")} INR · live-converted
                </div>
              )}

              <div className="mt-6 space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Full name</label>
                  <input
                    data-testid="checkout-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm"
                    placeholder="Nadia Okafor"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Email</label>
                  <input
                    data-testid="checkout-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-md border border-border bg-background px-3 text-sm"
                    placeholder="you@brand.com"
                  />
                </div>
              </div>

              <Button
                data-testid="checkout-pay-btn"
                onClick={openRazorpay}
                disabled={busy || !email || !name}
                className="w-full h-12 mt-6 rounded-full"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {busy ? "Opening secure checkout…" : `Pay ${displayPrice}`}
              </Button>

              <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secured by Razorpay · Cards · UPI · Netbanking · Wallets
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                After payment we'll help you create your workspace in seconds.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
